import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFeatureFlags } from "@/hooks/useFeatureFlag";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type StaffRole = "super_admin" | "support" | "content_editor";

/**
 * Returns the set of staff roles for the current user.
 * Empty array = not a staff member (regular subscriber).
 */
export const useStaffRoles = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // perf_large_data (read without useIsStaff to avoid a hook cycle): keep the
  // roles for 5 minutes; mounting a component no longer forces a refetch.
  const { data: flags } = useFeatureFlags();
  const flag = (flags as any[] | undefined)?.find((f) => f.key === "perf_large_data");
  const cachedRoles = queryClient.getQueryData<StaffRole[]>(["my-staff-roles", user?.id]);
  const perf = !!flag && (flag.enabled_for_all || (flag.enabled_for_staff && (cachedRoles?.length ?? 0) > 0));
  return useQuery({
    queryKey: ["my-staff-roles", user?.id],
    enabled: !!user,
    staleTime: perf ? 5 * 60 * 1000 : 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: perf ? undefined : true,
    queryFn: async (): Promise<StaffRole[]> => {
      if (!user) return [];
      const { data, error } = await (supabase as any).rpc("get_my_staff_roles");
      if (error) {
        console.error("get_my_staff_roles error", error);
        return [];
      }
      return (data || []) as StaffRole[];
    },
  });
};

export const useIsStaff = () => {
  const { data, isLoading } = useStaffRoles();
  return { isStaff: (data?.length ?? 0) > 0, roles: data ?? [], isLoading };
};

export const useHasStaffRole = (role: StaffRole) => {
  const { roles, isLoading } = useIsStaff();
  return { allowed: roles.includes(role), isLoading };
};

export const useHasAnyStaffRole = (allowed: StaffRole[]) => {
  const { roles, isLoading } = useIsStaff();
  return { allowed: roles.some((r) => allowed.includes(r)), isLoading };
};
